import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet, Text, View, TextInput, TouchableOpacity, ScrollView, ActivityIndicator,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import Data from './Data';
import { useScale, ScaleFn } from './common';

const C = {
  bg: '#F9FAFB', card: '#FFFFFF', cardInner: '#F3F4F6',
  border: '#E5E7EB', text: '#111827', textSub: '#4B5563',
  muted: '#9CA3AF', accent: '#38BDF8', err: '#EF4444',
};

// 「교체는 일어났는데 알림은 안 울린」 구간을 찾아 사유까지 붙여 보여준다.
// 판정은 서버가 한다 — adminserver/missed_alarm.js. 센서분석 화면의 패널과 같은 함수를
// 쓰므로 두 화면의 결론이 어긋나지 않는다.
//
// ★두 단계로 나눠 부른다.
//   목록(getMissedAlarmList) : SENSOR_HIS 만 읽어 "누구에게 몇 건" — 전 센서 0.1초대
//   사유(getMissedAlarms)    : 펼친 센서 한 대만 CAP 까지 읽어 A~E 분류 — 1~2초
//   전 센서의 CAP 를 한꺼번에 읽으면 60대 기준 1분이 넘어 화면이 못 쓰게 된다.
//
// 앵커는 SENSOR_HIS.AMOUNT = -1(자동교체 감지)다. 그래서 요양보호사가 센서를 직접
// 떼어 옮긴 「수동 교체」는 여기 안 잡힌다 — 그 경우 -1 이 찍히지 않는다.
const KIND_INFO: any = {
  A: { label: '한쪽 채널', color: '#C2410C', bg: '#FFEDD5' },
  B: { label: '임계 미달', color: '#1D4ED8', bg: '#DBEAFE' },
  C: { label: '기준값 고착', color: '#6D28D9', bg: '#EDE9FE' },
  D: { label: '부착 불량', color: '#B91C1C', bg: '#FEE2E2' },
  E: { label: '알림 지연', color: '#374151', bg: '#E5E7EB' },
  // 센서가 놓친 게 아니라 사람이 먼저 발견해 갈아버린 경우 — 미알림에서 빼야 한다
  M: { label: '교체가 먼저', color: '#2E7D32', bg: '#E8F5E9' },
  // 자동교체로 기록되지 않아 일반 판정에서 아예 빠지던 구간 — 목록 건수에는 안 잡힌다
  P: { label: '교체 흔적', color: '#00838F', bg: '#E0F7FA' },
};

type Ev = {
  KIND: string; TITLE: string; DETAIL: string; TIME: string;
  CAP1_RISE: number; CAP2_RISE: number;
  CAP1_RISE_NORMAL: number; CAP2_RISE_NORMAL: number;
  MAX_AMOUNT: number | null; ALARM_LEVEL: number;
  CONFIDENCE?: string | null; UNSURE?: string | null;
  HOLD_MIN?: number | null; DROP_MIN?: number | null;
};
type Row = {
  SENSOR_ID: string; NAME: string; AREA: string; ROOM_NO: string;
  COUNT: number; ALARM_LEVEL: number; NORMAL_CYCLES: number; TIMES: string[];
};
// 센서별 사유는 펼칠 때 받아 캐시한다 (같은 센서를 접었다 펴도 다시 안 부른다)
type Detail = { loading: boolean; events?: Ev[]; message?: string };

const fmtDate = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);

export default function MissedAlarm() {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);

  const [rows, setRows] = useState<Row[]>([]);
  const [scanned, setScanned] = useState<number>(0);
  const [missedTotal, setMissedTotal] = useState<number>(0);
  const [details, setDetails] = useState<{ [k: string]: Detail }>({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  // 기본값은 「어제 하루」 — 아침에 전날을 확인하는 쓰임새다.
  const [startDate, setStartDate] = useState<string>(fmtDate(yesterday));
  const [endDate, setEndDate] = useState<string>(fmtDate(yesterday));
  const [open, setOpen] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setMsg(null);
    setDetails({});
    setOpen(null);
    Data.fetchData('sensor/getMissedAlarmList', {
      STARTDATE: startDate + ' 00:00:00',
      ENDDATE: endDate + ' 23:59:59',
    }, (result: Row[], count: any, raw: any) => {
      setRows(count > 0 ? (result || []) : []);
      setScanned(raw?.scanned || 0);
      setMissedTotal(raw?.missedTotal || 0);
      if (count < 0) setMsg('조회 중 오류가 발생했습니다.');
      else if (!count) setMsg(raw?.message || '이 기간에는 미알림이 없습니다.');
      setLoading(false);
    }, () => { setMsg('조회 중 오류가 발생했습니다.'); setLoading(false); });
  };

  // 펼칠 때만 그 센서의 사유를 받아온다
  const toggle = (r: Row) => {
    if (open === r.SENSOR_ID) { setOpen(null); return; }
    setOpen(r.SENSOR_ID);
    if (details[r.SENSOR_ID]) return;
    setDetails((d) => ({ ...d, [r.SENSOR_ID]: { loading: true } }));
    Data.fetchData('sensor/getMissedAlarms', {
      SENSOR_ID: r.SENSOR_ID,
      STARTDATE: startDate + ' 00:00:00',
      ENDDATE: endDate + ' 23:59:59',
    }, (result: Ev[], count: any, raw: any) => {
      setDetails((d) => ({
        ...d,
        [r.SENSOR_ID]: {
          loading: false,
          events: count > 0 ? (result || []) : [],
          message: count > 0 ? undefined : (raw?.message || '사유를 판정하지 못했습니다'),
        },
      }));
    }, () => setDetails((d) => ({ ...d, [r.SENSOR_ID]: { loading: false, message: '사유 조회 실패' } })));
  };

  useEffect(() => { if (isFocused) load(); }, [isFocused]);

  const q = query.trim().toLowerCase();
  const view = rows.filter((r) =>
    !q || [r.NAME, r.AREA, r.ROOM_NO, r.SENSOR_ID].filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q)));

  return (
    <ScrollView style={styles.page} contentContainerStyle={{ padding: s(16) }}>
      <Text style={styles.title}>미알림 판독</Text>
      <Text style={styles.desc}>
        기저귀 교체는 일어났는데 젖음 알림이 울리지 않은 구간을 찾아 사유를 붙입니다.
        그래프를 읽을 줄 몰라도 어디를 손봐야 하는지 알 수 있게 만든 화면입니다.
      </Text>

      <View style={styles.dateRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>시작</Text>
          <TextInput value={startDate} onChangeText={setStartDate} style={styles.input} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.label}>종료</Text>
          <TextInput value={endDate} onChangeText={setEndDate} style={styles.input} placeholder="YYYY-MM-DD" />
        </View>
        <View style={{ flex: 1.4 }}>
          <Text style={styles.label}>검색</Text>
          <TextInput value={query} onChangeText={setQuery} style={styles.input} placeholder="이름 / 구역 / 호실 / 센서ID" />
        </View>
        <TouchableOpacity onPress={load} style={styles.loadBtn}>
          <Text style={styles.loadBtnText}>조회</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.hint}>
        ※ 「자동교체 감지」를 기준으로 찾습니다. 요양보호사가 센서를 직접 떼어 옮긴 수동 교체는
        감지 기록이 남지 않아 여기 나오지 않습니다. 목록의 건수는 후보입니다 — 「자세히」에서
        사람이 먼저 발견해 갈아버린 건(교체가 먼저)은 빠지고, 자동교체로 기록되지 않아
        목록에 못 잡힌 구간(교체 흔적)은 더해집니다.
      </Text>

      {!loading && scanned ? (
        <View style={styles.tallyRow}>
          <View style={[styles.tallyBox, { backgroundColor: missedTotal ? '#FEE2E2' : '#DCFCE7' }]}>
            <Text style={[styles.tallyNum, { color: missedTotal ? '#B91C1C' : '#15803D' }]}>{missedTotal}</Text>
            <Text style={[styles.tallyLabel, { color: missedTotal ? '#B91C1C' : '#15803D' }]}>미알림 후보</Text>
          </View>
          <View style={[styles.tallyBox, { backgroundColor: C.cardInner }]}>
            <Text style={[styles.tallyNum, { color: C.text }]}>{rows.length}</Text>
            <Text style={[styles.tallyLabel, { color: C.textSub }]}>해당 어르신</Text>
          </View>
          <View style={styles.tallyMeta}>
            <Text style={styles.tallyMetaText}>검사한 센서 {scanned}대</Text>
            <Text style={styles.tallyMetaText}>사유는 「자세히」에서 계산합니다</Text>
          </View>
        </View>
      ) : null}

      {msg && !loading ? <Text style={styles.note}>{msg}</Text> : null}

      {loading ? (
        <View style={styles.center}><ActivityIndicator color={C.accent} /></View>
      ) : view.length === 0 ? null : (
        <View style={styles.table}>
          <View style={[styles.tr, styles.thead]}>
            <Text style={[styles.th, { flex: 1 }]}>구역</Text>
            <Text style={[styles.th, { flex: 1 }]}>호실</Text>
            <Text style={[styles.th, { flex: 1.1 }]}>이름</Text>
            <Text style={[styles.th, { flex: 1.1 }]}>센서 ID</Text>
            <Text style={[styles.th, { flex: 0.7 }]}>건수</Text>
            <Text style={[styles.th, { flex: 2 }]}>발생 시각</Text>
            <Text style={[styles.th, { flex: 0.8 }]}> </Text>
          </View>
          {view.map((r) => (
            <View key={r.SENSOR_ID}>
              <View style={styles.tr}>
                <Text style={[styles.td, { flex: 1 }]}>{r.AREA}</Text>
                <Text style={[styles.td, { flex: 1 }]}>{r.ROOM_NO}</Text>
                <Text style={[styles.td, styles.name, { flex: 1.1 }]}>{r.NAME}</Text>
                <Text style={[styles.td, styles.mono, { flex: 1.1 }]}>{r.SENSOR_ID}</Text>
                <Text style={[styles.td, styles.name, { flex: 0.7 }]}>{r.COUNT}건</Text>
                <Text style={[styles.td, styles.mono, { flex: 2 }]} numberOfLines={2}>
                  {(r.TIMES || []).map((t) => String(t).slice(5, 16).replace('T', ' ')).join(', ')}
                </Text>
                <View style={{ flex: 0.8, alignItems: 'flex-end', paddingRight: s(10) }}>
                  <TouchableOpacity onPress={() => toggle(r)} style={styles.openBtn}>
                    <Text style={styles.openBtnText}>{open === r.SENSOR_ID ? '접기' : '자세히'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
              {open === r.SENSOR_ID ? (
                <View style={styles.detailBox}>
                  {details[r.SENSOR_ID]?.loading ? (
                    <View style={{ paddingVertical: s(12), alignItems: 'center' }}>
                      <ActivityIndicator color={C.accent} />
                      <Text style={styles.detailMeta}>사유를 판정하는 중입니다</Text>
                    </View>
                  ) : details[r.SENSOR_ID]?.message ? (
                    <Text style={styles.detailMeta}>{details[r.SENSOR_ID]?.message}</Text>
                  ) : (details[r.SENSOR_ID]?.events || []).map((e, i) => (
                    <View key={i} style={[styles.detailItem, i > 0 && styles.detailItemTop]}>
                      <View style={styles.detailHead}>
                        <Text style={[styles.badge, { backgroundColor: KIND_INFO[e.KIND]?.bg, color: KIND_INFO[e.KIND]?.color }]}>
                          {e.KIND}
                        </Text>
                        <Text style={styles.detailTitle}>
                          {String(e.TIME).slice(5, 16).replace('T', ' ')}  {e.TITLE}
                        </Text>
                        {e.CONFIDENCE ? (
                          <Text style={[styles.badge, e.CONFIDENCE === '애매'
                            ? { backgroundColor: '#FEE2E2', color: '#B91C1C' }
                            : { backgroundColor: '#FEF3C7', color: '#B45309' }]}>
                            {e.CONFIDENCE}
                          </Text>
                        ) : null}
                      </View>
                      <Text style={styles.detailText}>{e.DETAIL}</Text>
                      {e.UNSURE ? (
                        <Text style={[styles.detailMeta, { color: '#B45309' }]}>※ {e.UNSURE}</Text>
                      ) : null}
                      <Text style={styles.detailMeta}>
                        {e.MAX_AMOUNT != null
                          ? `단계 ${e.MAX_AMOUNT} (알림 기준 ${e.ALARM_LEVEL}) · `
                          : `유지 ${e.HOLD_MIN}분 · 하강 ${e.DROP_MIN}분 · `}
                        상승 {e.CAP1_RISE}/{e.CAP2_RISE} (평소 {e.CAP1_RISE_NORMAL}/{e.CAP2_RISE_NORMAL})
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  page: { flex: 1, backgroundColor: C.bg },
  title: { fontSize: s(20), fontWeight: '700', color: C.text, marginBottom: s(6) },
  desc: { fontSize: s(13), color: C.textSub, marginBottom: s(14), lineHeight: s(20) },
  dateRow: { flexDirection: 'row', alignItems: 'flex-end', gap: s(10), marginBottom: s(10) },
  label: { fontSize: s(12), color: C.textSub, marginBottom: s(4) },
  input: {
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: s(8),
    paddingHorizontal: s(10), paddingVertical: s(8), fontSize: s(13), color: C.text,
  },
  loadBtn: { backgroundColor: C.accent, borderRadius: s(8), paddingHorizontal: s(16), paddingVertical: s(9) },
  loadBtnText: { color: '#FFFFFF', fontWeight: '700', fontSize: s(13) },
  hint: { fontSize: s(12), color: C.muted, marginBottom: s(12), lineHeight: s(18) },
  note: { fontSize: s(13), color: C.textSub, marginBottom: s(10) },
  center: { paddingVertical: s(40), alignItems: 'center' },
  tallyRow: { flexDirection: 'row', flexWrap: 'wrap', gap: s(8), marginBottom: s(14), alignItems: 'center' },
  tallyBox: { borderRadius: s(8), paddingHorizontal: s(14), paddingVertical: s(8), minWidth: s(92) },
  tallyNum: { fontSize: s(20), fontWeight: '700' },
  tallyLabel: { fontSize: s(11), fontWeight: '700', marginTop: s(2) },
  tallyMeta: { marginLeft: s(6) },
  tallyMetaText: { fontSize: s(12), color: C.muted },
  table: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: s(10), overflow: 'hidden' },
  tr: { flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: C.border, minHeight: s(46) },
  thead: { backgroundColor: C.cardInner },
  th: { fontSize: s(12), fontWeight: '700', color: C.textSub, paddingHorizontal: s(10) },
  td: { fontSize: s(13), color: C.text, paddingHorizontal: s(10), paddingVertical: s(8) },
  name: { fontWeight: '700' },
  mono: { fontSize: s(12), color: C.textSub },
  badge: {
    fontSize: s(11), fontWeight: '700', textAlign: 'center',
    paddingVertical: s(3), paddingHorizontal: s(6), borderRadius: s(4), overflow: 'hidden',
  },
  openBtn: { backgroundColor: C.accent, borderRadius: s(6), paddingHorizontal: s(10), paddingVertical: s(6) },
  openBtnText: { color: '#FFFFFF', fontWeight: '700', fontSize: s(12) },
  detailBox: { backgroundColor: C.cardInner, paddingHorizontal: s(14), paddingVertical: s(10),
               borderBottomWidth: 1, borderBottomColor: C.border },
  detailItem: { paddingVertical: s(8) },
  detailItemTop: { borderTopWidth: 1, borderTopColor: C.border },
  detailHead: { flexDirection: 'row', alignItems: 'center', gap: s(8), marginBottom: s(4) },
  detailTitle: { fontSize: s(13), fontWeight: '700', color: C.text },
  detailText: { fontSize: s(12.5), color: C.textSub, lineHeight: s(19) },
  detailMeta: { fontSize: s(11.5), color: C.muted, marginTop: s(4) },
});
